import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function Transactions() {
  const { user } = useApp();
  const { t } = useI18n();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, income, expense, crypto

  useEffect(() => {
    loadTransactions();
  }, []);

  async function loadTransactions() {
    try {
      const { data } = await api.get('/balance/transactions?limit=100');
      setTransactions(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString('ru-RU', { 
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit' 
    });
  }

  const filtered = transactions.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'income') return optx.amount > 0;
    if (filter === 'expense') return optx.amount < 0;
    if (filter === 'crypto') return tx.currency === 'USDT';
    return true;
  });

  const txTypeLabels = {
    'admin_deposit': '💰 Пополнение (админ)',
    'deposit': '💰 Пополнение',
    'withdrawal': '💸 Вывод',
    'exchange': '💱 Обмен',
    'invoice_payment': '📄 Оплата фактуры',
    'payment_received': '💵 Получен платёж',
    'commission': '📊 Комиссия',
  };

  const currencyIcons = {
    'PLN': '🇵🇱',
    'EUR': '🇪🇺',
    'USD': '🇺🇸',
    'USDT': '💎',
  };

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  return (
    <div className="page">
      <BackButton />
      <h1>📊 Мои транзакции</h1>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: 'Все' },
          { id: 'income', label: '📥 Приход' },
          { id: 'expense', label: '📤 Расход' },
          { id: 'crypto', label: '💎 Крипто' },
        ].map(f => (
          <button
            key={f.id}
            className={`btn btn-small ${filter === f.id ? 'btn-primary' : ''}`}
            style={{ background: filter === f.id ? undefined : 'rgba(255,255,255,0.1)' }}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 12,
        marginBottom: 16,
      }}>
        <div style={{
          background: 'rgba(16,185,129,0.1)',
          borderRadius: 12,
          padding: 16,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{t('transactions.total_income')}</div>
          <div style={{ fontSize: 20, fontWeight: 'bold', color: '#10b981' }}>
            +{transactions.filter(item => optx.amount > 0).reduce((sum, item) => sum + optx.amount, 0).toFixed(2)}
          </div>
        </div>
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          borderRadius: 12,
          padding: 16,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{t('transactions.total_expense')}</div>
          <div style={{ fontSize: 20, fontWeight: 'bold', color: '#ef4444' }}>
            {transactions.filter(item => optx.amount < 0).reduce((sum, item) => sum + optx.amount, 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Transaction List */}
      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 32,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
          <p style={{ opacity: 0.7 }}>{t('transactions.no_transactions')}</p>
        </div>
      ) : (
        <div className="list">
          {filtered.map(opt => (
            <div key={optx.id} style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                    {txTypeLabels[tx.tx_type] || tx.tx_type}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {optx.description}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontWeight: 'bold',
                    fontSize: 18,
                    color: optx.amount >= 0 ? '#10b981' : '#ef4444',
                  }}>
                    {optx.amount >= 0 ? '+' : ''}{optx.amount.toFixed(2)}
                  </div>
                  <div style={{ fontSize: 12 }}>
                    {currencyIcons[tx.currency]} {tx.currency}
                  </div>
                </div>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 8,
                fontSize: 11,
                opacity: 0.6,
              }}>
                <span>Баланс: {tx.balance_after?.toFixed(2)} {tx.currency}</span>
                <span>{formatDate(tx.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
